import { AxiosResponse } from "axios";
import { axiosRequest } from "../api.utils";
import { API_ROOT } from "services/baseURLs.config";
import { toSelectedEmployment } from "./staff.employmentSelection.logic";

export interface StaffWorkGroup {
  companyUnitId: number | null;
  companyUnitName: string | null;
  workGroupId: number;
  workGroupName: string | null;
}

export interface StaffItem {
  workerId: number;
  externalId: string | null;
  firstName: string | null;
  lastName: string | null;
  role: string | null;
  paymentType?: string | null;
  phoneNumber: string | null;
  employmentType: string;
  isActive: boolean;
  endDate: string;
  workGroupId: number | null;
  workGroups: StaffWorkGroup[];
  personalIdentityNumber: string | null;
  status: StaffEmploymentStatus;
  employments?: Array<{
    startDate: string | null;
    endDate: string | null;
    isActive: boolean;
  }>;
}

export interface GetStaffResponse {
  items: StaffItem[];
  total: number;
}

export interface EmploymentTemplateListItem {
  id: number;
  name: string | null;
}

export interface EmploymentTemplateSalary {
  id: number;
  companyEmploymentTemplateId: number;
  name: string | null;
  requirementType: string | null;
  requirement: number;
  value: number;
}

export interface EmploymentTemplate {
  id: number;
  name: string | null;
  employmentType: string | null;
  employeeCategory: string | null;
  salaryType: string | null;
  salaryValue: number | null;
  workPercentage: number | null;
  paymentType: string | null;
  role: string | null;
  agreement: string | null;
  vacationDays: number | null;
  noticeDays: number | null;
  workplace: string | null;
  probationPeriodMonths: number | null;
  companyId: number | null;
  salaries: EmploymentTemplateSalary[] | null;
}

export interface StaffProfileWorkGroup {
  id: number;
  name: string | null;
  companyUnitId: number | null;
  companyUnitName: string | null;
}

export interface DeleteWorkerRequest {
  companyId: number;
  workerId: number;
}

export interface StaffProfileEmploymentCurrent {
  id: number | null;
  employmentType: string | null;
  salaryType: string | null;
  salary: number | null;
  salaries: StaffProfileResponseSalary[];
  startDate: string | null;
  endDate: string | null;
  workPercentage: number | null;
  taxTable: number | null;
  paymentType: string | null;
  role: string | null;
  employeeCategory: string | null;
  employeeNumber: number | null;
  agreement: string | null;
  vacationDays: number | null;
  noticeDays: number | null;
  workplace: string | null;
  probationPeriodEndDate: string | null;
}

export interface StaffProfileEmploymentHistory {
  id: number;
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;
  employmentType: string | null;
  employeeNumber: number | null;
  employeeCategory: string | null;
  salaryType: string | null;
  workPercentage: number | null;
  paymentType: string | null;
  role: string | null;
  agreement: string | null;
  vacationDays: number | null;
  noticeDays: number | null;
  workplace: string | null;
  probationPeriodEndDate: string | null;
  salaries: StaffProfileResponseSalary[];
}

export interface StaffProfileEmploymentInfo {
  current: StaffProfileEmploymentCurrent | null;
  endDate: string | null;
}

export interface StaffProfileDocument {
  fileName: string;
  downloadUrl: string;
}

export interface StaffProfileStat {
  stat: string;
  value: number;
}

export interface StaffProfileTax {
  id: number;
  startDate: string | null;
  type: string | null;
  value: number | null;
  isActive: boolean;
}

export type StaffPaymentMethodType = "BankAccount" | "InternationalBankAccount";
export type CreateWorkerPaymentMethodType =
  | "bankAccount"
  | "InternationalBankAccount";

export interface StaffProfilePaymentMethod {
  id: number;
  type: StaffPaymentMethodType | null;
  value: string | null;
  countryCode: string | null;
}

export interface StaffProfileData {
  isActive: boolean;
  isDeletable: boolean;
  externalId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  personalIdentityNumber: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  description: string | null;
  isRegistered: boolean;
  workPercentage: number | null;
  workGroups: StaffProfileWorkGroup[];
  employments: StaffProfileEmploymentHistory[];
  employmentInfo: StaffProfileEmploymentInfo | null;
  skills: string[];
  stats: StaffProfileStat[];
  taxes: StaffProfileTax[];
  paymentMethods: StaffProfilePaymentMethod[];
  documents: StaffProfileDocument[];
}

export interface StaffProfileResponseSkill {
  skill: string;
  experience: number;
}

export interface StaffProfileResponseStat {
  stat: string;
  value: number;
}

export interface StaffProfileResponseWorker {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  personalIdentityNumber: string | null;
  externalId: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  countryId: number | null;
  description: string | null;
  location: string | null;
  skills: StaffProfileResponseSkill[];
  stats: StaffProfileResponseStat[];
}

export interface StaffProfileResponseSalary {
  id: number;
  startDate: string | null;
  value: number | null;
  isActive: boolean;
}

export interface StaffProfileResponseEmployment {
  id: number;
  startDate: string | null;
  endDate: string | null;
  employmentType: string | null;
  employeeNumber: number | null;
  employeeCategory: string | null;
  salaryType: string | null;
  workPercentage: number | null;
  paymentType: string | null;
  role: string | null;
  agreement: string | null;
  vacationDays: number | null;
  noticeDays: number | null;
  workplace: string | null;
  probationPeriodEndDate: string | null;
  isActive: boolean;
  currentSalary: StaffProfileResponseSalary | null;
  salaries: StaffProfileResponseSalary[];
}

export interface StaffProfileResponseTax {
  id: number;
  startDate: string | null;
  type: string | null;
  value: number | null;
  isActive: boolean;
}

export interface StaffProfileResponsePaymentMethod {
  id: number;
  type: StaffPaymentMethodType | null;
  value: string | null;
  countryCode: string | null;
}

export interface StaffProfileResponseWorkGroup {
  workGroupId: number;
  workGroupName: string | null;
  companyUnitId: number | null;
  companyUnitName: string | null;
  companyEntityId: number | null;
  companyEntityName: string | null;
}

export interface StaffProfileResponseV3 {
  companyId: number;
  workerId: number;
  isActive: boolean;
  isRegistered: boolean;
  isDeletable: boolean;
  currentEmploymentId: number | null;
  worker: StaffProfileResponseWorker;
  currentEmployment: StaffProfileResponseEmployment | null;
  currentTax: StaffProfileResponseTax | null;
  workGroups: StaffProfileResponseWorkGroup[];
  employments: StaffProfileResponseEmployment[];
  taxes: StaffProfileResponseTax[];
  paymentMethods: StaffProfileResponsePaymentMethod[];
}

const toEmploymentInfo = (
  profile: StaffProfileResponseV3,
  selectedEmployment: StaffProfileResponseEmployment | null,
): StaffProfileEmploymentInfo | null => {
  if (!selectedEmployment) return null;

  const activeSalary =
    selectedEmployment.salaries.find((salary) => salary.isActive) ??
    selectedEmployment.currentSalary ??
    selectedEmployment.salaries[0] ??
    null;
  const activeTax =
    profile.taxes.find((tax) => tax.isActive) ?? profile.currentTax;

  return {
    current: {
      id: selectedEmployment.id,
      employmentType: selectedEmployment.employmentType,
      salaryType: selectedEmployment.salaryType,
      salary: activeSalary?.value ?? null,
      salaries: selectedEmployment.salaries ?? [],
      startDate: selectedEmployment.startDate,
      endDate: selectedEmployment.endDate,
      workPercentage: selectedEmployment.workPercentage,
      taxTable: activeTax?.value ?? null,
      paymentType: selectedEmployment.paymentType,
      role: selectedEmployment.role,
      employeeCategory: selectedEmployment.employeeCategory,
      employeeNumber: selectedEmployment.employeeNumber,
      agreement: selectedEmployment.agreement,
      vacationDays: selectedEmployment.vacationDays,
      noticeDays: selectedEmployment.noticeDays,
      workplace: selectedEmployment.workplace,
      probationPeriodEndDate: selectedEmployment.probationPeriodEndDate,
    },
    endDate: selectedEmployment.endDate,
  };
};

export const mapStaffProfileResponseV3ToData = (
  profile: StaffProfileResponseV3,
): StaffProfileData => {
  const selectedEmployment = toSelectedEmployment(profile);

  return {
    isActive: profile.isActive,
    isDeletable: profile.isDeletable,
    externalId: profile.worker.externalId,
    firstName: profile.worker.firstName,
    lastName: profile.worker.lastName,
    email: profile.worker.email,
    phoneNumber: profile.worker.phoneNumber,
    personalIdentityNumber: profile.worker.personalIdentityNumber,
    address: profile.worker.address,
    postalCode: profile.worker.postalCode,
    city: profile.worker.city,
    description: profile.worker.description,
    isRegistered: profile.isRegistered,
    workPercentage: selectedEmployment?.workPercentage ?? null,
    workGroups: profile.workGroups.map((workGroup) => ({
      id: workGroup.workGroupId,
      name: workGroup.workGroupName,
      companyUnitId: workGroup.companyUnitId,
      companyUnitName: workGroup.companyUnitName,
    })),
    employments: profile.employments.map((employment) => ({
      id: employment.id,
      isActive: employment.isActive,
      startDate: employment.startDate,
      endDate: employment.endDate,
      employmentType: employment.employmentType,
      employeeNumber: employment.employeeNumber,
      employeeCategory: employment.employeeCategory,
      salaryType: employment.salaryType,
      workPercentage: employment.workPercentage,
      paymentType: employment.paymentType,
      role: employment.role,
      agreement: employment.agreement,
      vacationDays: employment.vacationDays,
      noticeDays: employment.noticeDays,
      workplace: employment.workplace,
      probationPeriodEndDate: employment.probationPeriodEndDate,
      salaries: employment.salaries ?? [],
    })),
    employmentInfo: toEmploymentInfo(profile, selectedEmployment),
    skills: profile.worker.skills.map((skill) => skill.skill),
    stats: profile.worker.stats,
    taxes: profile.taxes.map((tax) => ({
      id: tax.id,
      startDate: tax.startDate,
      type: tax.type,
      value: tax.value,
      isActive: tax.isActive,
    })),
    paymentMethods: (profile.paymentMethods ?? []).map((paymentMethod) => ({
      id: paymentMethod.id,
      type: paymentMethod.type,
      value: paymentMethod.value,
      countryCode: paymentMethod.countryCode,
    })),
    documents: [],
  };
};

export type StaffSortBy = "worker" | "workGroup";
export type StaffEmploymentStatus =
  | "Active"
  | "Inactive"
  | "FutureEmployment"
  | "NoEmployment";
export type StaffStatus = "All" | "Active" | "Inactive";
export type StaffWorkGroupFilter = "InWorkGroup" | "All";

export interface GetStaffParams {
  page?: number;
  pageSize?: number;
  sortBy?: StaffSortBy;
  status?: StaffStatus;
  workGroupFilter?: StaffWorkGroupFilter;
  workGroupIds?: number[];
  workerIds?: number[];
}

export interface CreateStaffWorkerRequest {
  worker: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    personalIdentityNumber: string;
  };
  employment: {
    startDate: string;
    endDate: string | null;
    employmentType: string;
    employeeNumber: number;
    employeeCategory: string;
    salaryType: string;
    workPercentage: number;
    paymentType: string;
    role: string;
    agreement: string;
    vacationDays: number;
    noticeDays: number;
    workplace: string;
    probationPeriodEndDate: string | null;
    salaryValue: number;
    taxType: string;
    taxValue: number;
    paymentMethodType?: CreateWorkerPaymentMethodType;
    paymentMethodValue?: string;
  };
  workGroupIds: number[];
}

export interface UpdateWorkerEmploymentRequest {
  startDate: string;
  endDate: string | null;
  employmentType: string;
  employeeNumber: number;
  employeeCategory: string;
  salaryType: string;
  workPercentage: number;
  paymentType: string;
  role: string;
  agreement: string;
  vacationDays: number;
  noticeDays: number;
  workplace: string;
  probationPeriodEndDate: string | null;
}

export interface CreateWorkerEmploymentRequest {
  startDate: string;
  endDate: string | null;
  employmentType: string;
  employeeNumber: number;
  employeeCategory: string;
  salaryType: string;
  workPercentage: number;
  paymentType: string;
  role: string;
  agreement: string;
  vacationDays: number;
  noticeDays: number;
  workplace: string;
  probationPeriodEndDate: string | null;
  salaryValue: number;
}

export interface UpdateStaffWorkerTaxRequest {
  taxId: number;
  startDate: string;
  taxType: string;
  value: number;
}
export interface AddStaffWorkerTaxRequest {
  startDate: string;
  taxType: string;
  value: number;
}

export interface StaffWorkerPaymentMethodRequest {
  type: StaffPaymentMethodType;
  value: string;
}

export interface UpdateStaffWorkerSalaryRequest {
  salaryId: number;
  startDate: string;
  value: number;
}

export interface UpdateExistingStaffWorkerSalaryRequest {
  startDate: string;
  value: number;
}

export interface UpdateWorkerWorkgroupsRequest {
  workGroupIds: number[];
}

export interface UpdateWorkerProfileRequest {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  personalIdentityNumber: string;
}

const inFlightGetStaffRequests = new Map<
  string,
  Promise<AxiosResponse<GetStaffResponse>>
>();
const recentGetStaffResponses = new Map<
  string,
  { response: AxiosResponse<GetStaffResponse>; cachedAt: number }
>();
const GET_STAFF_RECENT_RESPONSE_TTL_MS = 1_500;

const serializeNumberList = (values: number[] | undefined): string => {
  return values && values.length > 0 ? values.join(",") : "";
};

const toGetStaffRequestKey = (
  companyId: number,
  query?: GetStaffParams,
): string => {
  return [
    companyId,
    query?.page ?? "",
    query?.pageSize ?? "",
    query?.sortBy ?? "",
    query?.status ?? "All",
    query?.workGroupFilter ?? "All",
    serializeNumberList(query?.workGroupIds),
    serializeNumberList(query?.workerIds),
  ].join("|");
};

const getStaff = (
  companyId: number,
  query?: GetStaffParams,
): Promise<AxiosResponse<GetStaffResponse>> => {
  const requestKey = toGetStaffRequestKey(companyId, query);
  const recentResponse = recentGetStaffResponses.get(requestKey);
  if (
    recentResponse &&
    Date.now() - recentResponse.cachedAt < GET_STAFF_RECENT_RESPONSE_TTL_MS
  ) {
    return Promise.resolve(recentResponse.response);
  }

  const inFlightRequest = inFlightGetStaffRequests.get(requestKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const params: {
    page?: number;
    pageSize?: number;
    sortBy?: StaffSortBy;
    status?: StaffStatus;
    workGroupFilter?: StaffWorkGroupFilter;
    workGroupIds?: string;
    workerIds?: string;
  } = {
    page: query?.page,
    pageSize: query?.pageSize,
    sortBy: query?.sortBy,
    status: query?.status ?? "All",
    workGroupFilter: query?.workGroupFilter,
    workGroupIds:
      query?.workGroupIds && query.workGroupIds.length > 0
        ? query.workGroupIds.join(",")
        : undefined,
    workerIds:
      query?.workerIds && query.workerIds.length > 0
        ? query.workerIds.join(",")
        : undefined,
  };

  const request = axiosRequest.auth
    .get<GetStaffResponse>(`${API_ROOT}/companies/${companyId}/workers/v3`, {
      params,
    })
    .then((response) => {
      recentGetStaffResponses.set(requestKey, {
        response,
        cachedAt: Date.now(),
      });
      return response;
    })
    .finally(() => {
      inFlightGetStaffRequests.delete(requestKey);
    });

  inFlightGetStaffRequests.set(requestKey, request);
  return request;
};

const createWorker = async (
  companyId: number,
  data: CreateStaffWorkerRequest,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<CreateStaffWorkerRequest>(
    `${API_ROOT}/companies/${companyId}/workers/v3`,
    data,
  );
};

const getWorkerProfile = async (
  companyId: number,
  workerId: number,
): Promise<AxiosResponse<StaffProfileResponseV3>> => {
  return await axiosRequest.auth.get<StaffProfileResponseV3>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3`,
  );
};

const getWorkerDocuments = async (
  companyId: number,
  workerId: number,
): Promise<AxiosResponse<StaffProfileDocument[]>> => {
  return await axiosRequest.auth.get<StaffProfileDocument[]>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v2/documents`,
  );
};

const updateWorkerEmployment = async (
  companyId: number,
  workerId: number,
  employmentId: number,
  data: UpdateWorkerEmploymentRequest,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<UpdateWorkerEmploymentRequest>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/employment/${employmentId}`,
    data,
  );
};

const createWorkerEmployment = async (
  companyId: number,
  workerId: number,
  data: CreateWorkerEmploymentRequest,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<CreateWorkerEmploymentRequest>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/employment`,
    data,
  );
};

const deleteWorkerEmployment = async (
  companyId: number,
  workerId: number,
  employmentId: number,
): Promise<AxiosResponse<void>> => {
  return await axiosRequest.auth.delete<void>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/employment/${employmentId}`,
  );
};

const addWorkerSalary = async (
  companyId: number,
  workerId: number,
  data: UpdateStaffWorkerSalaryRequest,
  employmentId: number,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<UpdateStaffWorkerSalaryRequest>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/employment/${employmentId}/salary`,
    data,
  );
};

const updateWorkerSalary = async (
  companyId: number,
  workerId: number,
  employmentId: number,
  salaryId: number,
  data: UpdateExistingStaffWorkerSalaryRequest,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<UpdateExistingStaffWorkerSalaryRequest>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/employment/${employmentId}/salary/${salaryId}`,
    data,
  );
};

const addWorkerTax = async (
  companyId: number,
  workerId: number,
  data: AddStaffWorkerTaxRequest,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<AddStaffWorkerTaxRequest>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/tax`,
    data,
  );
};

const addWorkerPaymentMethod = async (
  companyId: number,
  workerId: number,
  data: StaffWorkerPaymentMethodRequest,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post<StaffWorkerPaymentMethodRequest>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/payment`,
    data,
  );
};

const updateWorkerPaymentMethod = async (
  companyId: number,
  workerId: number,
  paymentMethodId: number,
  data: StaffWorkerPaymentMethodRequest,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<StaffWorkerPaymentMethodRequest>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/payment/${paymentMethodId}`,
    data,
  );
};

const deleteWorkerSalary = async (
  companyId: number,
  workerId: number,
  employmentId: number,
  salaryId: number,
): Promise<AxiosResponse<void>> => {
  return await axiosRequest.auth.delete<void>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/employment/${employmentId}/salary/${salaryId}`,
  );
};

const deleteWorkerTax = async (
  companyId: number,
  workerId: number,
  taxId: number,
): Promise<AxiosResponse<void>> => {
  return await axiosRequest.auth.delete<void>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/tax/${taxId}`,
  );
};

const deleteWorkerPaymentMethod = async (
  companyId: number,
  workerId: number,
  paymentMethodId: number,
): Promise<AxiosResponse<void>> => {
  return await axiosRequest.auth.delete<void>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/payment/${paymentMethodId}`,
  );
};

const updateWorkerWorkgroups = async (
  companyId: number,
  workerId: number,
  data: UpdateWorkerWorkgroupsRequest,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<UpdateWorkerWorkgroupsRequest>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/workGroups`,
    data,
  );
};

//Non-registered
const updateWorkerProfile = async (
  companyId: number,
  workerId: number,
  data: UpdateWorkerProfileRequest,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.put<UpdateWorkerProfileRequest>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3`,
    data,
  );
};

const deleteWorker = async (
  companyId: number,
  workerId: number,
): Promise<AxiosResponse<void>> => {
  return await axiosRequest.auth.delete<void>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3`,
  );
};

const getEmploymentTemplates = (
  companyId: number,
): Promise<AxiosResponse<EmploymentTemplateListItem[]>> => {
  return axiosRequest.auth.get<EmploymentTemplateListItem[]>(
    `${API_ROOT}/company/${companyId}/employments/templates`,
  );
};

const getEmploymentTemplate = (
  companyId: number,
  employmentTemplateId: number,
): Promise<AxiosResponse<EmploymentTemplate>> => {
  return axiosRequest.auth.get<EmploymentTemplate>(
    `${API_ROOT}/company/${companyId}/employments/templates/${employmentTemplateId}`,
  );
};

export const staff = {
  getStaff,
  createWorker,
  getWorkerProfile,
  getWorkerDocuments,
  updateWorkerEmployment,
  addWorkerSalary,
  updateWorkerSalary,
  addWorkerTax,
  addWorkerPaymentMethod,
  updateWorkerPaymentMethod,
  deleteWorkerEmployment,
  deleteWorkerSalary,
  deleteWorkerTax,
  deleteWorkerPaymentMethod,
  updateWorkerWorkgroups,
  createWorkerEmployment,
  updateWorkerProfile,
  deleteWorker,
  getEmploymentTemplates,
  getEmploymentTemplate,
};
